'use client';

import {
  GraduationCap,
  MessageCircle,
  Search,
  ShieldCheck,
  Sparkles,
  UserPlus2,
  UsersRound,
} from 'lucide-react';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';

const universities = ['ТГУ', 'ТПУ', 'ТУСУР', 'СибГМУ', 'ТГАСУ', 'ТГПУ'];

const audienceCards = [
  {
    title: 'Абитуриентам',
    description:
      'Найдите ребят, которые поступают на тот же факультет, заранее познакомьтесь с будущей группой и задайте первые вопросы без неловкости.',
    icon: GraduationCap,
  },
  {
    title: 'Студентам сейчас',
    description:
      'Соберите одногруппников в одном месте: обменяйтесь контактами, договоритесь о встрече, обсудите пары, дедлайны и студенческие события.',
    icon: UsersRound,
  },
  {
    title: 'Выпускникам прошлых лет',
    description:
      'Если вы учились в Томске давно, попробуйте найти свою группу, восстановить связь и снова почувствовать атмосферу студенческого города.',
    icon: Sparkles,
  },
] as const;

const trustNotes = [
  'Вы указываете вуз, год поступления, специальность и номер группы — так поиск становится точнее.',
  'После вступления можно перейти в чат группы и продолжить общение уже внутри личного профиля.',
  'Раздел подходит для поиска знакомых перед первым учебным днём, во время учёбы и спустя годы после выпуска.',
] as const;

type GroupCard = {
  id: string;
  university: string;
  admissionYear: number;
  specialty: string;
  groupNumber: string;
  _count?: { members: number };
};

type MyGroup = { id: string };
type GroupPick = Pick<
  GroupCard,
  'id' | 'university' | 'admissionYear' | 'specialty' | 'groupNumber'
>;
async function parseResponse<T>(
  res: Response,
): Promise<{ ok: boolean; data: T | null; error: string | null }> {
  const raw = await res.text();
  try {
    const data = raw ? (JSON.parse(raw) as T) : null;
    if (!res.ok) return { ok: false, data, error: `HTTP ${res.status}` };
    return { ok: true, data, error: null };
  } catch {
    return {
      ok: false,
      data: null,
      error: `Сервер вернул не JSON (HTTP ${res.status}).`,
    };
  }
}

export default function ClassmatesPage() {
  const { data: session, status } = useSession();

  const [query, setQuery] = useState({
    university: '',
    admissionYear: '',
    specialty: '',
    groupNumber: '',
  });
  const [groups, setGroups] = useState<GroupCard[]>([]);
  const [myGroupIds, setMyGroupIds] = useState<Set<string>>(new Set());
  const [selectedGroup, setSelectedGroup] = useState<GroupPick | null>(null);
  const [joinedGroupId, setJoinedGroupId] = useState<string | null>(null);
  const [joinName, setJoinName] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [form, setForm] = useState({
    university: 'ТУСУР',
    admissionYear: '',
    specialty: '',
    groupNumber: '',
    fullName: '',
    phone: '',
  });

  const canUse = status === 'authenticated' && !!session?.user?.id;
  const years = useMemo(
    () => Array.from({ length: 55 }, (_, i) => `${new Date().getFullYear() - i}`),
    [],
  );

  const loadGroups = useCallback(async () => {
    const p = new URLSearchParams(Object.entries(query).filter(([, v]) => v));
    const res = await fetch(`/api/students/classmates?${p.toString()}`, {
      cache: 'no-store',
    });
    const parsed = await parseResponse<{ groups?: GroupCard[] }>(res);
    if (!parsed.ok || !parsed.data) return setError(parsed.error || 'Не удалось загрузить группы');
    setGroups(parsed.data.groups || []);
  }, [query]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    const loadMine = async () => {
      if (!canUse) return;
      const res = await fetch('/api/students/classmates/my-groups', {
        cache: 'no-store',
      });
      const parsed = await parseResponse<{ groups?: MyGroup[] }>(res);
      if (!parsed.ok || !parsed.data?.groups) return;
      setMyGroupIds(new Set(parsed.data.groups.map((g) => g.id)));
    };
    void loadMine();
  }, [canUse]);
  const joinedGroup = useMemo(
    () => groups.find((g) => g.id === joinedGroupId) ?? null,
    [groups, joinedGroupId],
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const res = await fetch('/api/students/classmates', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(form),
    });
    const parsed = await parseResponse<{ groupId?: string; error?: string }>(res);
    if (!parsed.ok)
      return setError(parsed.data?.error || parsed.error || 'Не удалось сохранить данные.');
    setSuccess('Вы добавлены в группу.');

    if (parsed.data?.groupId) setJoinedGroupId(parsed.data.groupId);
    await loadGroups();
    setSelectedGroup(null);
  }

  async function joinSelectedGroup() {
    if (!selectedGroup || !joinName.trim()) return;
    setJoining(true);
    setError(null);
    setSuccess(null);
    const res = await fetch('/api/students/classmates', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        university: selectedGroup.university,
        admissionYear: String(selectedGroup.admissionYear),
        specialty: selectedGroup.specialty,
        groupNumber: selectedGroup.groupNumber,
        fullName: joinName.trim(),
        phone: '',
      }),
    });
    const parsed = await parseResponse<{ error?: string }>(res);
    setJoining(false);
    if (!parsed.ok)
      return setError(parsed.data?.error || parsed.error || 'Не удалось вступить в группу.');
    setSuccess('Вы вступили в группу.');
    setJoinedGroupId(selectedGroup.id);
    setSelectedGroup(null);
    setJoinName('');
    await loadGroups();
    setMyGroupIds((prev) => new Set([...prev, selectedGroup.id]));
  }

  function openGroupCard(group: GroupCard) {
    if (myGroupIds.has(group.id)) {
      setJoinedGroupId(group.id);
      setSelectedGroup(null);
      return;
    }
    setSelectedGroup(group);
  }

  return (
    <main className="space-y-6 pb-10">
      {/* kept upper blocks minimal */}
      <section className="relative overflow-hidden rounded-[2rem] border border-cyan-300/20 bg-slate-950/70 p-5 shadow-[0_28px_90px_-40px_rgba(34,211,238,0.6)] backdrop-blur sm:p-7 lg:p-8">
        <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-cyan-300/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/4 h-72 w-72 rounded-full bg-indigo-400/20 blur-3xl" />
        <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_360px] lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-cyan-300/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.24em] text-cyan-100">
              <UsersRound className="h-4 w-4" />
              Студенческий Томск
            </div>
            <h1 className="mt-5 max-w-4xl text-3xl font-black leading-tight text-white sm:text-5xl">
              Найдите своих одногруппников в Томске
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-200 sm:text-lg">
              Учились в Томске давно, поступаете только сейчас или уже ходите на пары? Этот раздел
              помогает найти людей из вашей группы, познакомиться до начала учёбы, восстановить
              старые связи и быстрее почувствовать себя своим в большом студенческом городе.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#classmates-search"
                className="rounded-2xl bg-cyan-300 px-5 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-200"
              >
                Найти группу
              </a>
              <a
                href="#classmates-add"
                className="rounded-2xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-black text-slate-100 transition hover:border-cyan-200/45 hover:bg-white/10"
              >
                Добавить себя
              </a>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-5">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-indigo-300/20 p-3 text-indigo-100">
                <MessageCircle className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-300">Зачем это нужно</p>
                <p className="text-lg font-black text-white">
                  Группа начинается с первого «привет»
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-300">
              Здесь можно быстро понять, кто учится рядом с вами, найти будущих друзей, договориться
              о совместном заселении, спросить про преподавателей, пары, документы и всё то, о чём
              обычно пишут в первые студенческие чаты.
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-3 md:grid-cols-3">
        {audienceCards.map(({ title, description, icon: Icon }) => (
          <article
            key={title}
            className="rounded-3xl border border-white/10 bg-slate-950/55 p-5 transition hover:-translate-y-0.5 hover:border-cyan-300/40 hover:bg-slate-950/70"
          >
            <div className="mb-4 inline-flex rounded-2xl bg-cyan-300/10 p-3 text-cyan-100">
              <Icon className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-black text-white">{title}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-300">{description}</p>
          </article>
        ))}
      </section>

      <section className="rounded-3xl border border-emerald-300/20 bg-emerald-400/10 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="inline-flex w-fit rounded-2xl bg-emerald-300/15 p-3 text-emerald-100">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-black text-emerald-100">
              Как работает поиск одногруппников
            </h2>
            <div className="mt-3 grid gap-2 text-sm leading-6 text-emerald-50/90 lg:grid-cols-3">
              {trustNotes.map((note) => (
                <p
                  key={note}
                  className="rounded-2xl border border-emerald-200/15 bg-slate-950/25 p-3"
                >
                  {note}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-2xl border border-rose-400/40 bg-rose-500/10 p-3 text-sm text-rose-100">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-3 text-sm text-emerald-100">
          {success}
        </div>
      )}
      <section className="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_400px]">
        <div
          id="classmates-search"
          className="rounded-3xl border border-cyan-300/20 bg-slate-950/55 p-5 backdrop-blur"
        >
          <h2 className="flex items-center gap-2 text-xl font-black text-cyan-200">
            <Search className="h-5 w-5" /> Поиск групп
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            Введите то, что помните или знаете: вуз, год поступления, специальность, номер группы.
            Можно начать с одного поля, а потом уточнить фильтр.
          </p>
          <div className="mt-4 grid gap-2 md:grid-cols-2">
            <select
              value={query.university}
              onChange={(e) => setQuery((p) => ({ ...p, university: e.target.value }))}
              className="rounded-xl border border-cyan-200/15 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            >
              <option value="">Все вузы</option>
              {universities.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
            <input
              value={query.admissionYear}
              onChange={(e) => setQuery((p) => ({ ...p, admissionYear: e.target.value }))}
              placeholder="Год поступления"
              className="rounded-xl border border-cyan-200/15 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            />
            <input
              value={query.specialty}
              onChange={(e) => setQuery((p) => ({ ...p, specialty: e.target.value }))}
              placeholder="Специальность"
              className="rounded-xl border border-cyan-200/15 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            />
            <input
              value={query.groupNumber}
              onChange={(e) => setQuery((p) => ({ ...p, groupNumber: e.target.value }))}
              placeholder="Номер группы"
              className="rounded-xl border border-cyan-200/15 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            />
          </div>
          <button
            onClick={loadGroups}
            className="mt-3 rounded-xl bg-cyan-300 px-4 py-2 text-sm font-black text-slate-950 hover:bg-cyan-200"
          >
            Найти
          </button>
          <div className="mt-4 grid gap-2">
            {groups.length === 0 && (
              <div className="rounded-2xl border border-cyan-200/15 bg-slate-900/40 p-4 text-sm text-slate-300">
                Группы не найдены. Попробуйте изменить фильтры поиска.
              </div>
            )}
            {groups.map((g) => {
              const joined = myGroupIds.has(g.id);
              return (
                <button
                  key={g.id}
                  onClick={() => openGroupCard(g)}
                  className="rounded-2xl border border-cyan-200/15 bg-slate-900/70 p-3 text-left hover:border-cyan-300/50"
                >
                  <div className="text-sm font-black text-cyan-200">
                    {g.university} • {g.admissionYear} • {g.groupNumber}
                  </div>
                  <div className="text-sm text-slate-200">{g.specialty}</div>
                  <div className="mt-1 text-xs text-slate-300">
                    Участников: {g._count?.members ?? 0}
                  </div>
                  <div className="mt-2 inline-flex rounded-lg bg-cyan-400/20 px-2 py-1 text-xs font-semibold text-cyan-200">
                    {joined ? 'Открыть чат' : 'Вступить в группу'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <aside
          id="classmates-add"
          className="rounded-3xl border border-indigo-300/25 bg-slate-950/70 p-5"
        >
          <h2 className="flex items-center gap-2 text-xl font-black text-indigo-200">
            <UserPlus2 className="h-5 w-5" /> Добавить себя
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            Если вашей группы ещё нет, создайте её через эту форму. Так другим студентам и
            выпускникам будет проще найти вас.
          </p>
          <form onSubmit={submit} className="mt-4 grid gap-2">
            <select
              required
              value={form.university}
              onChange={(e) => setForm((p) => ({ ...p, university: e.target.value }))}
              className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            >
              {universities.map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
            <select
              required
              value={form.admissionYear}
              onChange={(e) => setForm((p) => ({ ...p, admissionYear: e.target.value }))}
              className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            >
              <option value="">Год поступления</option>
              {years.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
            <input
              required
              value={form.specialty}
              onChange={(e) => setForm((p) => ({ ...p, specialty: e.target.value }))}
              placeholder="Специальность"
              className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            />
            <input
              required
              value={form.groupNumber}
              onChange={(e) => setForm((p) => ({ ...p, groupNumber: e.target.value }))}
              placeholder="Номер группы"
              className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            />
            <input
              required
              value={form.fullName}
              onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
              placeholder="ФИО"
              className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            />
            <input
              required
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              placeholder="Телефон"
              className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            />
            <button
              disabled={!canUse}
              className="mt-1 rounded-xl bg-indigo-300 px-4 py-2 text-sm font-black text-slate-950 disabled:opacity-50"
            >
              Сохранить
            </button>
          </form>
        </aside>
      </section>
      {selectedGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4">
          <div className="w-full max-w-md rounded-3xl border border-cyan-200/20 bg-slate-900 p-6">
            <h3 className="text-lg font-black text-cyan-100">Вступить в группу</h3>
            <p className="mt-2 text-sm text-slate-200">
              {selectedGroup.university} • {selectedGroup.admissionYear} •{' '}
              {selectedGroup.groupNumber}
            </p>
            <p className="text-sm text-slate-300">{selectedGroup.specialty}</p>
            <input
              value={joinName}
              onChange={(e) => setJoinName(e.target.value)}
              placeholder="Введите ФИО"
              className="mt-4 w-full rounded-xl border border-white/10 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            />
            <div className="mt-4 grid gap-2">
              <button
                onClick={joinSelectedGroup}
                disabled={!canUse || joining || !joinName.trim()}
                className="rounded-xl bg-cyan-300 px-4 py-3 text-base font-black text-slate-950 disabled:opacity-50"
              >
                {joining ? 'Вступаем...' : 'Вступить в группу'}
              </button>
              <button
                onClick={() => setSelectedGroup(null)}
                className="rounded-xl border border-white/15 px-4 py-2 text-slate-200"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
      {joinedGroup && (
        <Link
          href={`/profile/messages?groupId=${joinedGroup.id}`}
          className="block rounded-2xl bg-cyan-300 px-6 py-4 text-center text-lg font-black text-slate-950"
        >
          Перейти в чат группы {joinedGroup.groupNumber}
        </Link>
      )}
    </main>
  );
}
